import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Plus, FilePlus, Trash } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createLesson, deleteLesson, updateLesson, updateLessonOrder } from '@/services/lessons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LessonData } from '@/hooks/useCourseEditor';

interface LessonListProps {
  courseId: string;
  lessons: LessonData[];
  setLessons: React.Dispatch<React.SetStateAction<LessonData[]>>;
}

const LessonList: React.FC<LessonListProps> = ({ courseId, lessons, setLessons }) => {
  const [isCreatingLesson, setIsCreatingLesson] = useState(false);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState<LessonData | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const handleAddLesson = async () => {
    if (!newLessonTitle.trim()) {
      toast.error('Please enter a lesson title');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const maxOrder = lessons.length > 0
        ? Math.max(...lessons.map(lesson => lesson.orderIndex))
        : -1;
      
      // Create a new lesson using the API
      const newLesson = await createLesson({
        title: newLessonTitle,
        courseId: courseId,
        orderIndex: maxOrder + 1,
        content: '',
        quizQuestions: []
      });
      
      if (newLesson) {
        setLessons([...lessons, {
          id: newLesson.id,
          title: newLesson.title,
          orderIndex: newLesson.orderIndex,
          content: newLesson.content || null
        }]);
        setNewLessonTitle('');
        setIsCreatingLesson(false);
        
        toast.success('Lesson added successfully');
      } else {
        throw new Error('Failed to create lesson - no data returned');
      }
    } catch (error) {
      console.error('Error adding lesson:', error);
      toast.error('Failed to add lesson');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteLesson = async (lessonId: string) => {
    try {
      // Delete the lesson using the API
      await deleteLesson(lessonId);
      
      // Remove the deleted lesson from local state
      const remainingLessons = lessons.filter(lesson => lesson.id !== lessonId);
      
      // Sort the remaining lessons by order index
      const sortedLessons = [...remainingLessons].sort((a, b) => a.orderIndex - b.orderIndex);
      
      // Update order indexes to ensure no gaps (e.g., if lesson 2 is deleted, lesson 3 becomes lesson 2, etc.)
      const reorderedLessons = sortedLessons.map((lesson, index) => ({
        ...lesson,
        orderIndex: index
      }));
      
      // If order has changed, update the order on the server
      const orderChanged = reorderedLessons.some((lesson, index) => 
        lesson.orderIndex !== sortedLessons[index].orderIndex
      );
      
      if (orderChanged) {
        // Create lesson updates for the API
        const lessonUpdates = reorderedLessons.map(lesson => ({
          id: lesson.id,
          orderIndex: lesson.orderIndex
        }));
        
        // Update order indexes on the server to maintain sequential numbering
        await updateLessonOrder(lessonUpdates);
      }
      
      // Update state with reordered lessons
      setLessons(reorderedLessons);
      toast.success('Lesson deleted successfully');
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error('Failed to delete lesson');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, lesson: LessonData) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem(lesson);
    
    // This is needed for Firefox
    e.dataTransfer.setData('text/plain', lesson.id);
    
    // Add drag appearance
    if (e.currentTarget instanceof HTMLElement) {
      setTimeout(() => {
        e.currentTarget.classList.add('opacity-50');
      }, 0);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.classList.remove('opacity-50');
    }
    setDraggedItem(null);
  };

  const handleDragOver = (e: React.DragEvent, overLesson: LessonData) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === overLesson.id) {
      return;
    }
    
    // Reorder the lessons
    const orderedLessons = [...lessons].sort((a, b) => a.orderIndex - b.orderIndex);
    const draggedIndex = orderedLessons.findIndex(l => l.id === draggedItem.id);
    const overIndex = orderedLessons.findIndex(l => l.id === overLesson.id);
    
    if (draggedIndex === -1 || overIndex === -1) return;
    
    // Remove the dragged item
    orderedLessons.splice(draggedIndex, 1);
    // Insert at the new position
    orderedLessons.splice(overIndex, 0, draggedItem);
    
    // Update the order indexes
    const reorderedLessons = orderedLessons.map((lesson, index) => ({
      ...lesson,
      orderIndex: index
    }));
    
    setLessons(reorderedLessons);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    setIsReordering(true);
    
    const orderedLessons = [...lessons].sort((a, b) => a.orderIndex - b.orderIndex);
    
    // Create an array of lessons with their new order indexes
    const lessonUpdates = orderedLessons.map(lesson => ({
      id: lesson.id,
      orderIndex: lesson.orderIndex
    }));
    
    // Update the lesson order on the server
    updateLessonOrder(lessonUpdates)
      .then(() => {
        toast.success('Lesson order updated successfully');
      })
      .catch(error => {
        console.error('Error updating lesson order:', error);
        toast.error('Failed to update lesson order');
        
        // Reset to the original order
        setLessons([...lessons].sort((a, b) => a.orderIndex - b.orderIndex));
      })
      .finally(() => {
        setIsReordering(false);
      });
  };

  // Sort lessons by order index
  const sortedLessons = [...lessons].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Course Lessons</CardTitle>
          <CardDescription>
            Manage and organize the lessons for this course.
          </CardDescription>
        </div>
        <Button onClick={() => setIsCreatingLesson(true)} className="flex-shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Lesson
        </Button>
      </CardHeader>
      <CardContent>
        {isReordering && (
          <div className="mb-4 rounded-md bg-blue-50 p-3 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            <p className="text-sm">Updating lesson order...</p>
          </div>
        )}
      
        {isCreatingLesson ? (
          <div className="mb-6 rounded-lg border p-4">
            <h3 className="mb-2 text-sm font-medium">New Lesson</h3>
            <div className="space-y-4">
              <Input
                placeholder="Enter lesson title"
                value={newLessonTitle}
                onChange={(e) => setNewLessonTitle(e.target.value)}
              />
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreatingLesson(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddLesson} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="h-4 w-4 mr-2 animate-spin rounded-full border-b-2 border-white"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Lesson'
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
        
        {lessons.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <FilePlus className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium">No lessons yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Start by adding your first lesson to this course.
            </p>
            <Button onClick={() => setIsCreatingLesson(true)} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Add First Lesson
            </Button>
          </div>
        ) : (
          <ul className="space-y-2">
            {sortedLessons.map((lesson) => (
              <li 
                key={lesson.id} 
                className="flex items-center justify-between rounded-md border px-4 py-3 transition-opacity duration-200"
                draggable
                onDragStart={(e) => handleDragStart(e, lesson)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, lesson)}
                onDrop={handleDrop}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="flex h-8 w-8 cursor-move items-center justify-center text-muted-foreground"
                    title="Drag to reorder"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-grip-vertical h-4 w-4 text-muted-foreground">
                      <circle cx="9" cy="12" r="1"></circle>
                      <circle cx="9" cy="5" r="1"></circle>
                      <circle cx="9" cy="19" r="1"></circle>
                      <circle cx="15" cy="12" r="1"></circle>
                      <circle cx="15" cy="5" r="1"></circle>
                      <circle cx="15" cy="19" r="1"></circle>
                    </svg>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {lesson.orderIndex + 1}
                  </div>
                  <span className="font-medium">{lesson.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/admin/lesson/${lesson.id}`}>
                      Edit Content
                    </Link>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to={`/admin/lesson/${lesson.id}`}>
                          Edit Lesson
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteLesson(lesson.id)}
                      >
                        <Trash className="mr-2 h-4 w-4" />
                        Delete Lesson
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default LessonList;
